import React, { useCallback, useEffect, useRef, useState } from 'react';

import { link, routes } from '@aline/routes';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { IconButton, Typography } from '@mui/material';
import type { DataGridPremiumProps, GridApi, GridInitialState } from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridClasses,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import * as Sentry from '@sentry/react';
import { Helmet } from 'react-helmet';
import { Link, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMount } from 'react-use';
import type { DisplayMode, GridReport, ReportGridColDef, ReportRow } from 'src/components';
import {
  AddReportColumn,
  AlineButton,
  AlineCheckbox,
  allRowStatusFilters,
  ColumnHeaderMenu,
  ConfirmationDialog,
  ContentWrapper,
  DocumentGridContextProviders,
  DocumentsListFooter,
  EditableNameVariant,
  getColDefsFromReport,
  getColumnsRemovedFromReport,
  getGridStateToRestore,
  getServerStateFromReport,
  HIDDEN_REPORT_RUNTIME_COLUMN_FIELDS,
  LockConfirmationProvider,
  performReportSave,
  REPORT_ROW_STATUS_FIELD,
  ReportQuestionsProvider,
  Spacer,
  useTrackReportStateChanges,
} from 'src/components';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { EditIcon } from 'src/components/icons/EditIcon';
import { ReportEmptyIcon } from 'src/components/icons/ReportEmptyIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { openLink, useDispatchNotification } from 'src/helpers';
import {
  evictDocumentsFromReportRowsCache,
  FiltersAnchorProvider,
  useClearRowGroupingInRelationshipMode,
  useDescendantSelectionPropagation,
  useDocumentGrid,
  useDocumentGridCommonSx,
  useDropDeletedQuestionsFromGrouping,
  useFiltersAnchorState,
  useGetReportQuestions,
  useSaveReportState,
  useStickySelectAllAcrossFetches,
  useSubscribeToReportUpdates,
  useViewState,
} from 'src/hooks';
import type { AffectedRelationshipParents } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { ReportNameInput } from './report-detail/ReportNameInput';
import { ReportToolbar } from './report-detail/ReportToolbar';
import { ReportColumnsPanel } from './ReportColumnsPanel';
import { ReportFiltersPanel } from './ReportFiltersPanel';

const HIGHLIGHT_DURATION_MS = 7500;
const HIGHLIGHT_CELL_CLASS = 'Report-highlightCell';

const cellHighlightFade = keyframes`
  0% { background-color: var(--highlight-color) }
  100% { background-color: transparent }
`;

const useStyles = makeStyles()(theme => ({
  '@global': {
    [`.${HIGHLIGHT_CELL_CLASS}`]: {
      '--highlight-color': theme.palette.primary.light,
      animation: `${cellHighlightFade} ${HIGHLIGHT_DURATION_MS}ms ease-out forwards`,
    },
  },
  aiRemindersButton: {
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.5),
    },
    borderRadius: 8,
    marginRight: theme.spacing(1),
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  contentRow: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  hoverEffectRow: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  listContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  pageWrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  runAIButton: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  sparkleIcon: {
    height: 12,
    width: 12,
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      '&:hover #overflow-menu': {
        visibility: 'visible',
      },
    },
    flex: 1,
    width: '100%',
  },
}));

void gql`
  query GetReportToRestore($reportId: UUID!) {
    report(reportId: $reportId) {
      __typename
      id
      name
      state
      columnOptions {
        ... on FileFieldColumn {
          displayName
          field
          dataType
          isGroupable
        }
        ... on ExtractionReportColumn {
          questionId
          dataType
          displayName
          isGroupable
        }
      }
    }
  }
`;

interface ReportProps {
  reportId: UUID;
  onRowClick?: DataGridPremiumProps['onRowClick'];
}

export const reportRowHeightPx = 52;

interface ReportDataGridProps {
  apiRef: React.RefObject<GridApi | null>;
  dialogOpen: boolean;
  displayMode: DisplayMode;
  hasUnsavedChanges: boolean;
  missingMatchesOnly: boolean;
  onCloseDialog: () => void;
  onDiscardChanges: () => void;
  onDisplayModeChange: (mode: DisplayMode) => void;
  onSaveChanges: () => Promise<void>;
  report: GridReport;
  reportId: UUID;
  unconfirmedRelationshipsOnly: boolean;
  onRowClick?: DataGridPremiumProps['onRowClick'];
}

/**
 * Extracted DataGrid component that ensures grid state is restored before rendering.
 * This prevents:
 * - Autosave from firing before state restoration.
 * - Column definition flicker on initial mount.
 */
function ReportDataGrid({
  apiRef,
  dialogOpen,
  displayMode,
  hasUnsavedChanges,
  missingMatchesOnly,
  onCloseDialog,
  onDiscardChanges,
  onDisplayModeChange,
  onRowClick,
  onSaveChanges,
  report,
  reportId,
  unconfirmedRelationshipsOnly,
}: ReportDataGridProps): JSX.Element {
  const { classes } = useStyles();
  const client = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scrollToDoc] = useState(() => searchParams.get('scrollToDoc'));
  const [highlightCol] = useState(() => searchParams.get('highlightCol'));
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const handleRowSelectionModelChange = useDescendantSelectionPropagation(apiRef);
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const onNavigate = useCallback(
    (documentId: string) => openLink(link.DocumentDetail({ documentId })),
    []
  );

  // Compute restored state once so we can seed both useDocumentGrid and initialState
  const restoredState = useStable(() => getGridStateToRestore(report), [report.state]);
  const initialColumnVisibility = useStable(
    () =>
      ({
        ...restoredState.columns?.columnVisibilityModel,
        ...HIDDEN_REPORT_RUNTIME_COLUMN_FIELDS.reduce<Record<string, boolean>>((acc, field) => {
          acc[field] = false;
          return acc;
        }, {}),
      }) satisfies Record<string, boolean>,
    [restoredState]
  );

  // --- Shared document grid logic ---
  const {
    contextProviderProps,
    dataSource,
    dataSourceCache,
    documentCount,
    filterModel,
    getRowClassName,
    groupingColDef,
    handleRowClick,
    isGrouped,
    loadedRowCount,
    onDataSourceError,
    removeDocumentsFromGrid,
    removeGroupFromGrid,
    rowGroupingModel,
    setFilterModel,
    setRowGroupingModel,
    setSortModel,
    sortModel,
  } = useDocumentGrid({
    apiRef,
    displayMode,
    missingMatchesOnly,
    onNavigate,
    onRowClick,
    report,
    reportId,
    unconfirmedRelationshipsOnly,
  });

  /**
   * Grouping hides Document and shows it in the grouping column, dropping it to
   * the end of the order. Restore its position when grouping is cleared.
   */
  const nameColumnIndexRef = useRef<number | null>(null);
  const handleRowGroupingModelChange = useCallback(
    (model: string[]) => {
      const wasGrouped = rowGroupingModel.length > 0;
      if (!wasGrouped && model.length > 0) {
        nameColumnIndexRef.current = apiRef.current?.getColumnIndex('name', false) ?? null;
      }
      setRowGroupingModel(model);
      if (wasGrouped && model.length === 0 && nameColumnIndexRef.current != null) {
        apiRef.current?.setColumnIndex('name', nameColumnIndexRef.current);
      }
    },
    [apiRef, rowGroupingModel, setRowGroupingModel]
  );

  useClearRowGroupingInRelationshipMode({
    displayMode,
    rowGroupingModel,
    setRowGroupingModel,
  });
  // Through the change handler, so the Document column returns to its place when grouping empties.
  useDropDeletedQuestionsFromGrouping({
    rowGroupingModel,
    setRowGroupingModel: handleRowGroupingModelChange,
  });
  useStickySelectAllAcrossFetches(apiRef);

  const onDocumentsTrashed = useCallback(
    (
      documentIds: readonly UUID[],
      affectedRelationshipParents?: AffectedRelationshipParents | null
    ) => {
      evictDocumentsFromReportRowsCache({
        cache: client.cache,
        documentIds,
        reportId,
      });
      removeDocumentsFromGrid(documentIds, affectedRelationshipParents);
    },
    [client, removeDocumentsFromGrid, reportId]
  );

  const initialState = useStable((): GridInitialState => {
    /**
     * Pin the checkbox and the grouping column to the left so the group label
     * (e.g. document type) stays visible instead of floating to the far right.
     * Both grouping-field constants are pinned to cover tree-data and
     * row-grouping modes; an absent field is ignored by the grid.
     */
    const groupingFields = [GRID_TREE_DATA_GROUPING_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD];
    const alwaysLeft = [GRID_CHECKBOX_SELECTION_FIELD, ...groupingFields];
    const leftPinnedColumns = restoredState.pinnedColumns?.left ?? [];
    const leftWithCheckbox = [
      ...alwaysLeft,
      ...leftPinnedColumns.filter(field => !alwaysLeft.includes(field)),
    ];

    return {
      ...restoredState,
      columns: {
        ...restoredState.columns,
        columnVisibilityModel: initialColumnVisibility,
      },
      pagination: {
        paginationModel: { page: 0, pageSize: 50 },
      },
      pinnedColumns: {
        left: leftWithCheckbox,
        right: restoredState.pinnedColumns?.right ?? ['actions'],
      },
      rowGrouping: restoredState.rowGrouping,
    };
  }, [report.state]);
  const colDefsWithPinnedAction = useStable((): ReportGridColDef[] => {
    return [
      ...getColDefsFromReport(report),
      {
        __typename: 'FileFieldColumn',
        field: REPORT_ROW_STATUS_FIELD,
        filterOperators: allRowStatusFilters,
        filterable: true,
        // Derived per-row AI/OCR state, not document data — nothing to group on.
        groupable: false,
        headerName: 'Row Status',
        hideable: false,
        sortable: false,
      } satisfies ReportGridColDef,
      {
        __typename: 'FileFieldColumn',
        disableColumnMenu: true,
        field: 'actions',
        // Renders the add-column affordance rather than a value.
        groupable: false,
        headerAlign: 'center',
        minWidth: 0,
        renderHeader: params => <AddReportColumn {...params} reportId={reportId} />,
        resizable: false,
        sortable: false,
        width: 50,
      } satisfies ReportGridColDef,
    ];
  }, [reportId, report.columnOptions]);

  useEffect(() => {
    if (!scrollToDoc || !highlightCol || !apiRef.current) return;

    const unsubscribeFn = apiRef.current.subscribeEvent('rowsSet', async () => {
      const { data } = await client.query<
        Apollo.GetDocumentRowIndexQuery,
        Apollo.GetDocumentRowIndexQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetDocumentRowIndexDocument,
        variables: { documentId: scrollToDoc, filterModel, filterTimeZone, reportId, sortModel },
      });
      const rowIndex = data?.documentRowIndex;
      if (typeof rowIndex !== 'number' || rowIndex === -1 || !apiRef.current) return;

      setSearchParams(
        prev => {
          prev.delete('scrollToDoc');
          prev.delete('highlightCol');
          return prev;
        },
        { replace: true }
      );

      const col = apiRef.current
        .getAllColumns()
        .find(c => c.field?.toLowerCase() === highlightCol.toLowerCase());
      if (!col) return;

      apiRef.current.scrollToIndexes({
        colIndex: apiRef.current.getColumnIndex(col.field),
        rowIndex,
      });
      const selector = `.MuiDataGrid-row[data-id="${scrollToDoc}"] .MuiDataGrid-cell[data-field="${col.field}"]`;

      function waitForCell() {
        const cell = document.querySelector(selector);
        if (!cell) {
          requestAnimationFrame(waitForCell);
          return;
        }

        cell.classList.add(HIGHLIGHT_CELL_CLASS);
        setTimeout(() => cell.classList.remove(HIGHLIGHT_CELL_CLASS), HIGHLIGHT_DURATION_MS);
      }
      requestAnimationFrame(waitForCell);
      unsubscribeFn();
    });

    return () => unsubscribeFn?.();
  }, [
    scrollToDoc,
    highlightCol,
    apiRef,
    client,
    filterTimeZone,
    filterModel,
    reportId,
    setSearchParams,
    sortModel,
  ]);

  const commonSx = useDocumentGridCommonSx(displayMode, loadedRowCount);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <DocumentGridContextProviders {...contextProviderProps}>
        <DataGridPremium<ReportRow>
          checkboxSelection
          disableColumnFilter
          disableRowSelectionOnClick
          lazyLoading
          showToolbar
          apiRef={apiRef}
          className={classes.xGrid}
          columnHeaderHeight={reportRowHeightPx}
          columns={colDefsWithPinnedAction}
          dataSource={dataSource}
          dataSourceCache={dataSourceCache}
          filterModel={filterModel}
          getEstimatedRowHeight={() => reportRowHeightPx}
          getRowClassName={getRowClassName}
          getRowHeight={() => (displayMode === 'relationship' ? 'auto' : reportRowHeightPx)}
          groupingColDef={groupingColDef}
          initialState={initialState}
          paginationModel={{ page: 0, pageSize: 50 }}
          pinnedColumnsSectionSeparator="border"
          rowHeight={reportRowHeightPx}
          // `parents: false` is deliberate — see RepositoryAllDocuments.
          rowSelectionPropagation={isGrouped ? { descendants: true, parents: false } : undefined}
          slotProps={{
            columnsPanel: { reportId },
            footer: {
              isRelationshipGrouping: displayMode === 'relationship',
              missingMatchesOnly,
              onDocumentsTrashed,
              onGroupTrashed: removeGroupFromGrid,
              reportId,
              square: true,
              totalCount: documentCount,
              totalDocumentCount: documentCount,
            },
            loadingOverlay: {
              noRowsVariant: 'skeleton',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
            toolbar: {
              displayMode,
              hasUnsavedChanges,
              onClose: onCloseDialog,
              onDiscardChanges,
              onDisplayModeChange,
              onRowGroupingModelChange: handleRowGroupingModelChange,
              onSaveChanges,
              open: dialogOpen,
              reportId,
              reportName: report.name || 'Untitled Report',
              rowCount: documentCount,
              rowGroupingModel: [...rowGroupingModel],
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            columnMenu: ColumnHeaderMenu,
            columnMenuIcon: EditIcon,
            columnsPanel: ReportColumnsPanel,
            filterPanel: ReportFiltersPanel,
            footer: DocumentsListFooter,
            noRowsOverlay: ReportEmpty,
            toolbar: ReportToolbar,
          }}
          sortModel={sortModel}
          sx={theme => {
            return {
              ...commonSx(theme),

              [`&.${gridClasses.root}`]: {
                overflow: 'visible',
              },
              [`& .${gridClasses.cell}`]: {
                '&:focus-within': {
                  outline: 'none',
                },
                borderBottom: `1px solid ${theme.appColors.rule}`,
                padding: 0,
              },
              [`& .${gridClasses['cell--pinnedLeft']}`]: {
                borderRight: `1px solid ${theme.appColors.rule}`,
              },
              [`& .${gridClasses['cell--pinnedRight']}`]: {
                borderLeft: `1px solid ${theme.appColors.rule}`,
              },
              [`& .${gridClasses.columnHeaders}`]: {
                borderBottom: `1px solid ${theme.appColors.rule}`,
              },
              [`& .${gridClasses['columnHeader--pinnedLeft']}`]: {
                '&:focus-within': {
                  outline: 'none',
                },
                borderRight: `1px solid ${theme.appColors.rule}`,
              },
              [`& .${gridClasses['columnHeader--pinnedRight']}`]: {
                '&:focus-within': {
                  outline: 'none',
                },
                borderLeft: `1px solid ${theme.appColors.rule}`,
              },
              [`& .${gridClasses.columnHeader}`]: {
                '&:focus-within': {
                  outline: 'none',
                },
              },
              [`& .${gridClasses.main}`]: {
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.appColors.rule}`,
                borderBottom: 'none',
                overflow: 'hidden',
              },
            };
          }}
          treeData={isGrouped}
          onCellDoubleClick={(_params, event) => {
            // Prevent cell double click from editing cell.  We handle edits on single click, programmatically.
            event.defaultMuiPrevented = true;
          }}
          onDataSourceError={onDataSourceError}
          onFilterModelChange={setFilterModel}
          onRowClick={handleRowClick}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          onSortModelChange={setSortModel}
        />
      </DocumentGridContextProviders>
    </FiltersAnchorProvider>
  );
}

export function Report({ onRowClick, reportId }: ReportProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { openDrawer } = useGlobalDrawer();
  const apiRef = useGridApiRef();
  const { saveReport } = useSaveReportState();
  const { dispatch, displayMode, missingMatchesOnly, unconfirmedRelationshipsOnly } =
    useViewState();
  const isAutomationsRoute = Boolean(
    matchPath(routes.ReportReportDetailAutomations, location.pathname)
  );

  function handleToggleAutomations() {
    if (isAutomationsRoute) {
      navigate(link.ReportDetail({ reportId }), { replace: true });
    } else {
      navigate(link.ReportReportDetailAutomations({ reportId }), { replace: true });
      openDrawer();
    }
  }

  const handleDisplayModeChange = useCallback(
    (newMode: DisplayMode) => {
      dispatch({ mode: newMode, type: 'setDisplayMode' });
    },
    [dispatch]
  );

  const [searchParams] = useSearchParams();
  const { updateActiveTabTitle } = useTabContext();
  const [runAIDialogOpen, setRunAIDialogOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { data } = Apollo.useGetReportToRestoreQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { reportId },
  });
  const { questions } = useGetReportQuestions({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const dispatchManualSuccessNotification = useDispatchNotification({
    message: 'Report changes saved!',
    variant: 'success',
  });
  const dispatchManualFailureNotification = useDispatchNotification({
    message: 'Could not save report changes. Click Save to retry.',
    variant: 'error',
  });
  const dispatchAutoSaveFailureNotification = useDispatchNotification({
    message: 'Auto-save failed — your changes are preserved. Click Save to retry.',
    variant: 'error',
  });

  /**
   * Monotonic id bumped on every save dispatch and on Discard. Lets an
   * in-flight silent save short-circuit when a newer Save/Discard has
   * superseded it, so a slow auto-save response can't clobber a freshly
   * discarded or manually saved state.
   */
  const saveSeqRef = useRef(0);

  /**
   * Forward reference: the hook below needs an `onAutoSave` callback, and
   * `performSave` (defined after the hook call) needs `withSilentRestore`
   * from the hook's return value. The ref breaks that structural cycle.
   */
  const performSaveRef = useRef<(args: { silent: boolean }) => Promise<void>>(
    async () => undefined
  );

  const { cancelPendingAutoSave, withSilentRestore } = useTrackReportStateChanges({
    apiRef,
    onAutoSave: () => performSaveRef.current({ silent: true }),
    onStateChange: () => setHasUnsavedChanges(true),
    reportState: data?.report?.state ?? {},
  });

  const performSave = useCallback(
    async ({ silent }: { silent: boolean }): Promise<void> => {
      const report = data?.report;
      if (!apiRef.current || !report) return;

      const { malformed } = getServerStateFromReport(report);
      if (malformed) {
        Sentry.captureException(new Error('Report.state failed getIsGridState'), {
          extra: { reportId: report.id, silent },
          tags: { feature: 'report-auto-save' },
        });
      }

      let result;
      try {
        result = await performReportSave({
          gridApi: apiRef.current,
          report,
          reportId,
          saveReport,
          saveSeqRef,
          silent,
          withSilentRestore,
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { reportId, silent, site: 'performSave-outer' },
          tags: { feature: 'report-auto-save' },
        });
        if (silent) dispatchAutoSaveFailureNotification();
        else dispatchManualFailureNotification();
        setHasUnsavedChanges(true);
        return;
      }

      switch (result.kind) {
        case 'no-op':
        case 'superseded':
          return;
        case 'success':
          if (result.silent) {
            setHasUnsavedChanges(result.hasManualDirty);
          } else {
            setHasUnsavedChanges(false);
            dispatchManualSuccessNotification();
          }
          return;
        case 'no-data':
          Sentry.captureException(new Error('updateReport returned no data'), {
            extra: { reportId, silent: result.silent },
            tags: { feature: 'report-auto-save' },
          });
          if (result.silent) dispatchAutoSaveFailureNotification();
          else dispatchManualFailureNotification();
          setHasUnsavedChanges(true);
          return;
        case 'error':
          Sentry.captureException(result.error, {
            extra: { reportId, silent: result.silent },
            tags: { feature: 'report-auto-save' },
          });
          if (result.silent) dispatchAutoSaveFailureNotification();
          else dispatchManualFailureNotification();
          setHasUnsavedChanges(true);
      }
    },
    [
      apiRef,
      data,
      reportId,
      saveReport,
      dispatchManualSuccessNotification,
      dispatchManualFailureNotification,
      dispatchAutoSaveFailureNotification,
      withSilentRestore,
    ]
  );
  performSaveRef.current = performSave;

  const handleSaveChanges = useCallback(async (): Promise<void> => {
    cancelPendingAutoSave();
    await performSave({ silent: false });
  }, [cancelPendingAutoSave, performSave]);

  const handleDiscardChanges = useCallback((): void => {
    if (!apiRef.current || !data?.report) return;
    cancelPendingAutoSave();
    // Invalidate any auto-save whose mutation is already in flight: it will
    // see a stale `saveSeqRef.current` after `await` and return `superseded`.
    saveSeqRef.current++;
    const report = data.report;
    withSilentRestore(() => {
      apiRef.current?.restoreState(getGridStateToRestore(report));
    });
    setHasUnsavedChanges(false);
  }, [apiRef, cancelPendingAutoSave, data, withSilentRestore]);

  useSubscribeToReportUpdates(reportId);

  // Redirect legacy ?highlightAutomation=<id> deep links to the new route.
  useMount(() => {
    const highlightAutomation = searchParams.get('highlightAutomation');
    if (highlightAutomation) {
      navigate(
        `${link.ReportReportDetailAutomations({ reportId })}?highlightAutomation=${highlightAutomation}`,
        {
          replace: true,
        }
      );
    }
  });

  useEffect(() => {
    if (data?.report) setWarningModalOpen(Boolean(getColumnsRemovedFromReport(data.report).length));
  }, [data?.report]);

  useEffect(() => {
    const name = data?.report?.name;
    if (typeof name === 'string') updateActiveTabTitle(name || 'Untitled Report');
  }, [data?.report?.name, updateActiveTabTitle]);

  return (
    <>
      <Helmet>
        <title>{data?.report?.name || 'Untitled Report'}</title>
      </Helmet>
      <div className={classes.pageWrapper}>
        <ContentWrapper>
          <div className={classes.contentRow}>
            <div className={classes.mainContent}>
              <div className={classes.toolbar}>
                <IconButton
                  className={classes.backButton}
                  component={Link}
                  size="small"
                  to={link.ReportIndex()}
                >
                  <BackArrowIcon />
                </IconButton>
                <ReportNameInput reportId={reportId} variant={EditableNameVariant.LargeText} />
                <Spacer />
                <AlineButton
                  className={classes.aiRemindersButton}
                  color="primary"
                  size="small"
                  startIcon={<SmallAiSparkleIcon className={classes.sparkleIcon} />}
                  variant="outlined"
                  onClick={handleToggleAutomations}
                >
                  AI Reminders
                </AlineButton>
                <AlineButton
                  buttonClassName={classes.runAIButton}
                  color="primary"
                  variant="contained"
                  onClick={() => setRunAIDialogOpen(true)}
                >
                  Run Aline AI
                </AlineButton>
              </div>
              <ReportQuestionsProvider questions={questions}>
                <LockConfirmationProvider>
                  <div className={classes.listContainer}>
                    {data?.report && (
                      <ReportDataGrid
                        apiRef={apiRef}
                        dialogOpen={runAIDialogOpen}
                        displayMode={displayMode}
                        hasUnsavedChanges={hasUnsavedChanges}
                        missingMatchesOnly={missingMatchesOnly}
                        report={data.report}
                        reportId={reportId}
                        unconfirmedRelationshipsOnly={unconfirmedRelationshipsOnly}
                        onCloseDialog={() => setRunAIDialogOpen(false)}
                        onDiscardChanges={handleDiscardChanges}
                        onDisplayModeChange={handleDisplayModeChange}
                        onRowClick={onRowClick}
                        onSaveChanges={handleSaveChanges}
                      />
                    )}
                  </div>
                </LockConfirmationProvider>
              </ReportQuestionsProvider>
            </div>
          </div>
        </ContentWrapper>
      </div>
      <ConfirmationDialog
        disableAltClose
        message="It looks like a column was removed from your report. This can happen when a variable is deleted. Click save below to ensure your report is correct and up-to-date."
        open={warningModalOpen}
        primaryText="Save"
        showSecondary={false}
        title="Column removed from report"
        onClickPrimary={() => void handleSaveChanges()}
        onClose={() => setWarningModalOpen(false)}
      />
    </>
  );
}

const useEmptyStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

export function ReportEmpty(): JSX.Element {
  const { classes } = useEmptyStyles();

  return (
    <div className={classes.container}>
      <ReportEmptyIcon />
      <Typography align="center" variant="subtitle1">
        No documents
      </Typography>
      <Typography align="center" variant="body1">
        Adjust filters or search to change results
      </Typography>
    </div>
  );
}
